import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Api from "../Config/ApiConfig";

const AppConfig = () => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    ios_version: "",
    android_version: "",
    force_update: false,
    critical_update: false,
    update_message: "",
    critical_message: "",
    ios_store_url: "",
    android_store_url: "",
    send_notification: true
  });
  const [chatPolicy, setChatPolicy] = useState({
    enabled: true,
    windowHoursBeforeDeparture: 12
  });
  const [minPricing, setMinPricing] = useState({
    domestic: 35,
    international: 50
  });
  const [minPricingSaving, setMinPricingSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await Api.get("/app-version");
      if (response.data && response.data.data) {
        const data = response.data.data;
        setInitialValues({
          ios_version: data.ios_version || "",
          android_version: data.android_version || "",
          force_update: data.force_update || false,
          critical_update: data.critical_update || false,
          update_message: data.update_message || "",
          critical_message: data.critical_message || "",
          ios_store_url: data.ios_store_url || "",
          android_store_url: data.android_store_url || "",
          send_notification: true
        });
      }

      try {
        const chatResponse = await Api.get("/chat-policy");
        if (chatResponse.data && chatResponse.data.data) {
          const chatData = chatResponse.data.data;
          setChatPolicy({
            enabled: typeof chatData.enabled === "boolean" ? chatData.enabled : true,
            windowHoursBeforeDeparture:
              typeof chatData.windowMinutesBeforeDeparture === "number"
                ? Math.max(0, Math.round(chatData.windowMinutesBeforeDeparture / 60))
                : 12
          });
        }
      } catch (chatError) {
        console.error("Error fetching chat policy:", chatError);
      }

      try {
        const pricingRes = await Api.get("/minimum-pricing");
        if (pricingRes.data && pricingRes.data.data) {
          setMinPricing({
            domestic: Number(pricingRes.data.data.domestic) || 35,
            international: Number(pricingRes.data.data.international) || 50
          });
        }
      } catch (pricingErr) {
        console.error("Error fetching minimum pricing:", pricingErr);
      }
    } catch (error) {
      console.error("Error fetching app config:", error);
      toast.error("Failed to load app configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleMinimumPricingSave = async () => {
    const dom = Number(minPricing.domestic);
    const intl = Number(minPricing.international);
    if (!Number.isFinite(dom) || dom <= 0 || !Number.isFinite(intl) || intl <= 0) {
      toast.error("Please enter valid positive numbers for minimum pricing.");
      return;
    }
    try {
      setMinPricingSaving(true);
      const res = await Api.put("/minimum-pricing", {
        domestic: dom,
        international: intl
      });
      if (res.data) {
        toast.success(`Minimum pricing updated! Domestic: $${dom}, International: $${intl}`);
      }
    } catch (err) {
      console.error("Error saving minimum pricing:", err);
      toast.error(err?.response?.data?.message || "Failed to update minimum pricing");
    } finally {
      setMinPricingSaving(false);
    }
  };

  const handleChatPolicySave = async () => {
    try {
      setLoading(true);
      const payload = {
        enabled: chatPolicy.enabled,
        windowMinutesBeforeDeparture: Math.max(
          0,
          Number.isFinite(chatPolicy.windowHoursBeforeDeparture)
            ? chatPolicy.windowHoursBeforeDeparture * 60
            : 12 * 60
        )
      };
      const response = await Api.post("/chat-policy", payload);
      if (response.data && response.data.success) {
        toast.success("Chat policy updated successfully");
      } else {
        toast.error(response.data.message || "Failed to update chat policy");
      }
    } catch (error) {
      console.error("Error updating chat policy:", error);
      toast.error(error.response?.data?.message || "Failed to update chat policy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object({
      ios_version: Yup.string().required("iOS Version is required"),
      android_version: Yup.string().required("Android Version is required"),
      force_update: Yup.boolean(),
      critical_update: Yup.boolean(),
      update_message: Yup.string().required("Update message is required"),
      critical_message: Yup.string(),
      ios_store_url: Yup.string().url("Invalid URL"),
      android_store_url: Yup.string().url("Invalid URL"),
      send_notification: Yup.boolean()
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await Api.post("/app-version", values);
        if (response.data && response.data.success) {
          toast.success("App configuration updated successfully");
          fetchConfig(); // Refresh data
        } else {
          toast.error(response.data.message || "Failed to update configuration");
        }
      } catch (error) {
        console.error("Error updating app config:", error);
        toast.error(error.response?.data?.message || "Failed to update configuration");
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            {/* Header Section */}
            <div className="app-config-header mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h2 className="app-config-title mb-2">
                    <i className="fas fa-mobile-alt me-3"></i>
                    App Version Management
                  </h2>
                  <p className="app-config-subtitle mb-0">
                    Configure app versions, update settings, and manage user notifications
                  </p>
                </div>
                <div className="app-config-badge">
                  <span className="badge bg-success fs-6 px-3 py-2">
                    <i className="fas fa-check-circle me-2"></i>
                    Active Configuration
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={formik.handleSubmit}>
              {/* Version Information Card */}
              <div className="app-config-card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-code-branch me-2" style={{color: '#17C470'}}></i>
                    Version Information
                  </h5>
                  <small className="text-muted" style={{color: '#2B8B6F', fontWeight: '500'}}>Set the current app versions for both platforms</small>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="version-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fab fa-apple me-2"></i>iOS Version
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fab fa-apple text-dark"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.ios_version && formik.errors.ios_version ? 'is-invalid' : ''}`}
                            placeholder="e.g. 1.0.0"
                            {...formik.getFieldProps("ios_version")}
                          />
                        </div>
                        {formik.touched.ios_version && formik.errors.ios_version && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.ios_version}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="version-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fab fa-android me-2"></i>Android Version
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fab fa-android text-success"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.android_version && formik.errors.android_version ? 'is-invalid' : ''}`}
                            placeholder="e.g. 1.0.0"
                            {...formik.getFieldProps("android_version")}
                          />
                        </div>
                        {formik.touched.android_version && formik.errors.android_version && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.android_version}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Update Settings Card */}
              <div className="app-config-card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-cogs me-2" style={{color: '#FFC600'}}></i>
                    Update Settings
                  </h5>
                  <small className="text-muted" style={{color: '#2B8B6F', fontWeight: '500'}}>Configure update behavior and user experience</small>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="update-toggle-card">
                        <div className="form-check form-switch form-switch-lg">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="forceUpdate"
                            checked={formik.values.force_update}
                            onChange={(e) => formik.setFieldValue("force_update", e.target.checked)}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="forceUpdate">
                            <i className="fas fa-exclamation-triangle me-2" style={{color: '#FFC600'}}></i>
                            Force Update
                          </label>
                        </div>
                        <small className="text-muted d-block mt-2" style={{color: '#034328', fontWeight: '500'}}>
                          Users must update to continue using the app
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="update-toggle-card">
                        <div className="form-check form-switch form-switch-lg">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="criticalUpdate"
                            checked={formik.values.critical_update}
                            onChange={(e) => formik.setFieldValue("critical_update", e.target.checked)}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="criticalUpdate">
                            <i className="fas fa-shield-alt me-2" style={{color: '#FF7F50'}}></i>
                            Critical Update
                          </label>
                        </div>
                        <small className="text-muted d-block mt-2" style={{color: '#034328', fontWeight: '500'}}>
                          Security or critical bug fix update
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="update-toggle-card">
                        <div className="form-check form-switch form-switch-lg">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="sendNotification"
                            checked={formik.values.send_notification}
                            onChange={(e) => formik.setFieldValue("send_notification", e.target.checked)}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="sendNotification">
                            <i className="fas fa-bell me-2" style={{color: '#17C470'}}></i>
                            Send Notifications
                          </label>
                        </div>
                        <small className="text-muted d-block mt-2" style={{color: '#034328', fontWeight: '500'}}>
                          Send push notifications to all users
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Card */}
              <div className="app-config-card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-comment-alt me-2" style={{color: '#2B8B6F'}}></i>
                    User Messages
                  </h5>
                  <small className="text-muted" style={{color: '#2B8B6F', fontWeight: '500'}}>Customize messages shown to users during updates</small>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="message-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-info-circle me-2" style={{color: '#17C470'}}></i>
                          Regular Update Message
                        </label>
                        <textarea
                          className={`form-control message-textarea ${formik.touched.update_message && formik.errors.update_message ? 'is-invalid' : ''}`}
                          rows="3"
                          placeholder="Message to show users when an update is available"
                          {...formik.getFieldProps("update_message")}
                        />
                        {formik.touched.update_message && formik.errors.update_message && (
                          <div className="invalid-feedback">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.update_message}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="message-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-exclamation-triangle me-2" style={{color: '#FFC600'}}></i>
                          Critical Update Message
                        </label>
                        <textarea
                          className={`form-control message-textarea ${formik.touched.critical_message && formik.errors.critical_message ? 'is-invalid' : ''}`}
                          rows="3"
                          placeholder="Message for critical updates"
                          {...formik.getFieldProps("critical_message")}
                        />
                        {formik.touched.critical_message && formik.errors.critical_message && (
                          <div className="invalid-feedback">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.critical_message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Store URLs Card */}
              <div className="app-config-card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-store me-2" style={{color: '#034328'}}></i>
                    App Store Links
                  </h5>
                  <small className="text-muted" style={{color: '#2B8B6F', fontWeight: '500'}}>Configure store URLs for app downloads</small>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="store-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fab fa-app-store me-2"></i>iOS App Store URL
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fab fa-app-store text-primary"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.ios_store_url && formik.errors.ios_store_url ? 'is-invalid' : ''}`}
                            placeholder="https://apps.apple.com/..."
                            {...formik.getFieldProps("ios_store_url")}
                          />
                        </div>
                        {formik.touched.ios_store_url && formik.errors.ios_store_url && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.ios_store_url}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="store-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fab fa-google-play me-2"></i>Google Play Store URL
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-light">
                            <i className="fab fa-google-play text-success"></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control ${formik.touched.android_store_url && formik.errors.android_store_url ? 'is-invalid' : ''}`}
                            placeholder="https://play.google.com/store/..."
                            {...formik.getFieldProps("android_store_url")}
                          />
                        </div>
                        {formik.touched.android_store_url && formik.errors.android_store_url && (
                          <div className="invalid-feedback d-block">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.android_store_url}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Policy Card */}
              <div className="app-config-card mb-4">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-comments me-2" style={{ color: "#6f42c1" }}></i>
                    Chat Policy
                  </h5>
                  <small className="text-muted" style={{ color: "#2B8B6F", fontWeight: "500" }}>
                    Control when booking chat becomes available for users
                  </small>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="update-toggle-card">
                        <div className="form-check form-switch form-switch-lg">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="chatRestrictionEnabled"
                            checked={chatPolicy.enabled}
                            onChange={(e) =>
                              setChatPolicy((prev) => ({
                                ...prev,
                                enabled: e.target.checked
                              }))
                            }
                          />
                          <label
                            className="form-check-label fw-semibold"
                            htmlFor="chatRestrictionEnabled"
                          >
                            <i
                              className="fas fa-lock-clock me-2"
                              style={{ color: "#6f42c1" }}
                            ></i>
                            Enable time-based chat restriction
                          </label>
                        </div>
                        <small
                          className="text-muted d-block mt-2"
                          style={{ color: "#034328", fontWeight: "500" }}
                        >
                          When disabled, chat is always open for eligible bookings
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="version-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-clock me-2" style={{ color: "#6f42c1" }}></i>
                          Hours before departure when chat opens
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={chatPolicy.windowHoursBeforeDeparture}
                          onChange={(e) =>
                            setChatPolicy((prev) => ({
                              ...prev,
                              windowHoursBeforeDeparture: Number(e.target.value) || 0
                            }))
                          }
                          disabled={!chatPolicy.enabled}
                        />
                        <small className="text-muted d-block mt-2">
                          Set to 0 to allow chat at any time while the booking is active.
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary btn-lg px-4"
                      onClick={handleChatPolicySave}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Chat Policy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Minimum Pricing Floor Configuration */}
              <div className="version-card mb-4" style={{ borderLeft: "4px solid #10b981" }}>
                <div className="card-header bg-white border-0 pb-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h4 className="card-title mb-1 fw-bold">
                        <i className="fas fa-coins me-2 text-success"></i>
                        Minimum Fare Floor Rules (Dynamic)
                      </h4>
                      <p className="text-muted small mb-0">
                        Enforce a minimum floor price on all bookings across domestic and international routes.
                        Live updates take effect immediately in the API, simulator, and mobile apps.
                      </p>
                    </div>
                    <a
                      href="/pricing-simulator"
                      className="btn btn-sm btn-outline-success"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fas fa-calculator me-1"></i>
                      Pricing Simulator
                    </a>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="version-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-plane-departure me-2 text-success"></i>
                          Domestic Flight Minimum (USD)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="form-control"
                            value={minPricing.domestic}
                            onChange={(e) =>
                              setMinPricing((prev) => ({
                                ...prev,
                                domestic: e.target.value
                              }))
                            }
                          />
                        </div>
                        <small className="text-muted d-block mt-2">
                          Default: $35 USD. Applied when origin and destination are in the same country.
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="version-input-group">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-globe me-2 text-success"></i>
                          International Flight Minimum (USD)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="form-control"
                            value={minPricing.international}
                            onChange={(e) =>
                              setMinPricing((prev) => ({
                                ...prev,
                                international: e.target.value
                              }))
                            }
                          />
                        </div>
                        <small className="text-muted d-block mt-2">
                          Default: $50 USD. Applied for international cross-border routes.
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-success btn-lg px-4"
                      onClick={handleMinimumPricingSave}
                      disabled={minPricingSaving}
                    >
                      {minPricingSaving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          ></span>
                          Saving Pricing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Minimum Pricing
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="app-config-actions">
                <div className="d-flex justify-content-end gap-3">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-lg px-4"
                    onClick={() => fetchConfig()}
                    disabled={loading}
                  >
                    <i className="fas fa-undo me-2"></i>
                    Reset
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg px-5"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save Configuration
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </LoadingOverlay>

      <style jsx>{`
        .app-config-header {
          background: linear-gradient(135deg, #034328 0%, #2B8B6F 100%);
          border-radius: 14px;
          padding: 2rem;
          color: white;
          box-shadow: 0 2px 12px #e8f9f2;
          margin-bottom: 24px;
        }

        .app-config-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          font-family: MetropolisBold;
        }

        .app-config-subtitle {
          font-size: 1.1rem;
          opacity: 0.9;
          font-family: MetropolisRegular;
        }

        .app-config-badge .badge {
          border-radius: 25px;
          font-weight: 500;
          background: #17C470 !important;
        }

        .app-config-card {
          background: white;
          border-radius: 14px;
          box-shadow: 0 1px 6px #e8f9f2;
          border: 1px solid #e8f9f2;
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .app-config-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px #e8f9f2;
        }

        .app-config-card .card-header {
          background: #f6faf9;
          border-bottom: 1px solid #e8f9f2;
          padding: 1.5rem;
        }

        .app-config-card .card-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #034328;
          font-family: MetropolisSemiBold;
        }

        .app-config-card .card-body {
          padding: 2rem;
        }

        .version-input-group,
        .store-input-group,
        .message-input-group {
          margin-bottom: 1rem;
        }

        .version-input-group .input-group-text,
        .store-input-group .input-group-text {
          border: 1px solid #e8f9f2;
          font-weight: 500;
          background: #f6faf9;
        }

        .form-control {
          border: 1px solid #e8f9f2;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-size: 1rem;
          transition: all 0.2s ease;
          font-family: MetropolisRegular;
        }

        .form-control:focus {
          border-color: #17C470;
          box-shadow: 0 0 0 0.2rem rgba(23, 196, 112, 0.25);
        }

        .message-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .update-toggle-card {
          background: #f6faf9;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          border: 1px solid #e8f9f2;
          transition: all 0.2s ease;
        }

        .update-toggle-card:hover {
          border-color: #17C470;
          background: #f0f8f4;
        }

        .update-toggle-card .form-check-label {
          color: #034328 !important;
          font-weight: 600 !important;
          font-size: 1rem;
        }

        .update-toggle-card small {
          color: #2B8B6F !important;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .form-switch-lg .form-check-input {
          width: 3rem;
          height: 1.5rem;
          border-radius: 3rem;
        }

        .form-switch-lg .form-check-input:checked {
          background-color: #17C470;
          border-color: #17C470;
        }

        .app-config-actions {
          background: white;
          border-radius: 14px;
          padding: 2rem;
          box-shadow: 0 1px 6px #e8f9f2;
          border: 1px solid #e8f9f2;
        }

        .btn-lg {
          padding: 0.75rem 2rem;
          font-size: 1.1rem;
          font-weight: 500;
          border-radius: 8px;
          transition: all 0.2s ease;
          font-family: MetropolisSemiBold;
        }

        .btn-primary {
          background: #034328;
          border: 1px solid #034328;
          color: white;
        }

        .btn-primary:hover {
          background: #2B8B6F;
          border-color: #2B8B6F;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(3, 67, 40, 0.3);
        }

        .btn-outline-secondary {
          border: 1px solid #6c757d;
          color: #6c757d;
          background: transparent;
        }

        .btn-outline-secondary:hover {
          background: #6c757d;
          border-color: #6c757d;
          color: white;
          transform: translateY(-1px);
        }

        .invalid-feedback {
          font-size: 0.875rem;
          margin-top: 0.5rem;
          color: #FF7F50;
        }

        .text-purple {
          color: #6f42c1 !important;
        }

        .form-label {
          font-family: MetropolisSemiBold;
          color: #034328 !important;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .text-muted {
          color: #2B8B6F !important;
          font-weight: 500;
        }

        .card-header small {
          color: #2B8B6F !important;
          font-weight: 500;
        }

        .dashboard_data_wrap {
          background: #f6faf9;
          min-height: 100vh;
          padding: 2rem;
        }

        .dashbord_cont_wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .app-config-header {
            padding: 1.5rem;
          }
          
          .app-config-title {
            font-size: 1.5rem;
          }
          
          .app-config-card .card-body {
            padding: 1.5rem;
          }
          
          .update-toggle-card {
            margin-bottom: 1rem;
          }

          .dashboard_data_wrap {
            padding: 1rem;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AppConfig;
